import React from "react";
import { Filter, Loader2, Search as SearchIcon, X } from "lucide-react";
import { Input } from "../ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { SingleSelectCombobox } from "../SingleSelectCombobox";

const CONFIDENCE_OPTIONS = [
  { value: "ALL", label: "All confidence" },
  { value: "HIGH", label: "High only" },
  { value: "MEDIUM", label: "Medium only" },
  { value: "LOW", label: "Low only" },
  { value: "UNKNOWN", label: "Unknown only" },
];

/**
 * Floating filter panel for the Map page. Top-left overlay containing
 * the address search, county/city pickers, and confidence filter.
 * Purely presentational — all state lives on the parent.
 */
export function FilterPanel({
  searchQ,
  setSearchQ,
  searching,
  runSearch,
  county,
  handleCountyChange,
  facetsCounties,
  city,
  handleCityChange,
  cityOptions,
  confidence,
  setConfidence,
}) {
  return (
    <div
      data-testid="map-filters"
      className="absolute top-4 left-4 panel p-3 bg-zinc-950 border border-zinc-700 w-72 space-y-3 shadow-xl ring-1 ring-zinc-900/60"
      style={{ zIndex: 1000 }}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-mono text-zinc-500">
        <Filter className="w-3 h-3" />
        Filters
      </div>

      <div
        data-testid="map-tutorial-banner"
        className="text-[11px] leading-relaxed text-zinc-300 bg-emerald-500/8 border border-emerald-500/25 rounded-md p-2"
      >
        <span className="text-emerald-300 font-mono uppercase text-[9px] tracking-wider block mb-0.5">
          Plan a route
        </span>
        Click a marker →{" "}
        <span className="text-emerald-300">Add to route</span> → repeat →{" "}
        <span className="text-emerald-300">Plan in Google Maps</span> from the
        panel below.
      </div>

      <form onSubmit={runSearch} className="relative">
        <SearchIcon className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          data-testid="map-search-input"
          placeholder="Search address, city or ZIP…"
          value={searchQ}
          onChange={(e) => setSearchQ(e.target.value)}
          className="pl-8 pr-8 bg-zinc-900 border-zinc-800 text-zinc-100 h-9 text-xs"
        />
        {searching ? (
          <Loader2 className="w-3.5 h-3.5 absolute right-2.5 top-1/2 -translate-y-1/2 text-emerald-400 animate-spin" />
        ) : searchQ ? (
          <button
            type="button"
            onClick={() => setSearchQ("")}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
            aria-label="Clear search"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        ) : null}
      </form>

      <div>
        <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 mb-1">
          County
        </div>
        <SingleSelectCombobox
          testId="map-county-filter"
          value={county === "ALL" ? null : county}
          onChange={handleCountyChange}
          options={facetsCounties || []}
          allLabel="All counties"
          placeholder="Type a county name…"
        />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 mb-1">
          City
        </div>
        <SingleSelectCombobox
          testId="map-city-filter"
          value={city === "ALL" ? null : city}
          onChange={handleCityChange}
          options={cityOptions}
          allLabel={county !== "ALL" ? `All cities in ${county}` : "All cities"}
          placeholder="Type a city name…"
        />
      </div>

      <div>
        <div className="text-[10px] uppercase tracking-wider font-mono text-zinc-400 mb-1">
          Confidence
        </div>
        <Select value={confidence} onValueChange={setConfidence}>
          <SelectTrigger
            data-testid="map-confidence-filter"
            className="bg-zinc-900 border-zinc-800 text-zinc-100 h-9"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800 text-zinc-100">
            {CONFIDENCE_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}
